import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Phone, Mail, ArrowUpRight, Check, ImageIcon } from "lucide-react";
import { getServiceBySlug, SERVICES } from "@/data/services";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  useEffect(() => {
    if (service) {
      document.title = `${service.title} · ETANCIA`;
      window.scrollTo(0, 0);
    }
  }, [service]);

  if (!service) return <Navigate to="/" replace />;

  const Icon = service.icon;
  const otherServices = SERVICES.filter((s) => s.slug !== slug);

  return (
    <main data-testid={`service-detail-${slug}`} className="min-h-screen bg-white">
      <Navbar />

      {/* HERO SECTION */}
      <section className="relative isolate pt-32 md:pt-40 pb-16 md:pb-20 bg-[#003366] text-white overflow-hidden">
        <div className="absolute inset-0 diag-lines opacity-25 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <Link
            to="/#services"
            data-testid="back-to-services"
            className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-[#FF5A1F] mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Retour aux prestations
          </Link>

          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="inline-block w-10 h-px bg-[#FF5A1F]" />
                <span className="label-kicker text-[#FF9A6F]">
                  Expertise ETANCIA
                </span>
              </div>
              <h1 className="font-display font-extrabold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1]">
                {service.title}
              </h1>
              <p className="mt-6 text-base md:text-lg text-slate-200/90 leading-relaxed max-w-2xl">
                {service.longDesc}
              </p>
            </div>
            <div className="lg:col-span-4">
              <div className="w-20 h-20 bg-[#FF5A1F] flex items-center justify-center ml-auto">
                <Icon className="w-9 h-9 text-white" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-block w-10 h-px bg-[#FF5A1F]" />
              <span className="label-kicker text-[#FF5A1F]">Ce qui est inclus</span>
            </div>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight text-[#0F172A] leading-tight">
              Une prestation complète, sans mauvaise surprise.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
              {service.features.map((f, i) => (
                <div
                  key={i}
                  data-testid={`feature-${i}`}
                  className="flex items-start gap-3 border-b border-slate-200 pb-4"
                >
                  <div className="w-6 h-6 bg-[#003366] text-white flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5" strokeWidth={3} />
                  </div>
                  <span className="font-display font-semibold text-[15px] text-[#0F172A] leading-tight">
                    {f}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* REFERENCES / CHANTIERS */}
      <section
        data-testid="references-section"
        className="py-20 md:py-24 bg-[#F2F4F7]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-8 mb-12 items-end">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-block w-10 h-px bg-[#FF5A1F]" />
                <span className="label-kicker text-[#FF5A1F]">
                  Chantiers de référence
                </span>
              </div>
              <h2 className="font-display font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#0F172A] leading-[1]">
                Ils nous ont fait confiance.
              </h2>
            </div>
            <p className="lg:col-span-5 text-slate-600 leading-relaxed">
              Quelques chantiers récents réalisés pour cette expertise en
              Île-de-France.
            </p>
          </div>

          {service.references.length === 0 ? (
            <div className="border border-dashed border-slate-300 p-16 text-center bg-white">
              <p className="text-slate-500 font-display">
                Chantiers en cours d'ajout. Contactez-nous pour découvrir nos
                réalisations.
              </p>
            </div>
          ) : (
            <div className="space-y-16">
              {service.references.map((ref, i) => {
                const gallery = ref.images && ref.images.length > 0 ? ref.images : ref.image ? [ref.image] : [];
                return (
                  <article
                    key={i}
                    data-testid={`reference-${i}`}
                    className="bg-white border border-slate-200"
                  >
                    <div className="grid lg:grid-cols-12 gap-0">
                      <div className="lg:col-span-8 relative overflow-hidden bg-slate-100 aspect-[16/10] lg:aspect-auto lg:min-h-[440px]">
                        {gallery.length > 0 ? (
                          <img
                            src={gallery[0]}
                            alt={ref.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center text-slate-300 bg-gradient-to-br from-slate-100 to-slate-200">
                            <ImageIcon className="w-12 h-12 mb-2" />
                            <span className="text-[10px] uppercase tracking-[0.2em]">
                              Photo à venir
                            </span>
                          </div>
                        )}
                        <div className="absolute top-5 left-5 bg-[#003366] text-white px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold">
                          Chantier {ref.year}
                        </div>
                      </div>

                      <div className="lg:col-span-4 p-8 lg:p-10 flex flex-col justify-between border-t lg:border-t-0 lg:border-l border-slate-200">
                        <div>
                          <div className="label-kicker text-[#FF5A1F]">
                            {ref.location}
                          </div>
                          <h3 className="mt-3 font-display font-bold text-2xl text-[#0F172A] leading-tight">
                            {ref.title}
                          </h3>
                          <p className="mt-4 text-sm text-slate-600 leading-relaxed">
                            {ref.description}
                          </p>
                        </div>
                        <div className="mt-6 pt-6 border-t border-slate-200 flex items-center gap-3 text-xs uppercase tracking-[0.18em] text-slate-500">
                          <span className="font-display font-bold text-[#003366]">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <span>Réalisation ETANCIA</span>
                        </div>
                      </div>
                    </div>

                    {gallery.length > 1 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-t border-slate-200">
                        {gallery.slice(1).map((img, j) => (
                          <div
                            key={j}
                            className="aspect-[4/3] overflow-hidden border-r border-slate-200 last:border-r-0 bg-slate-100"
                          >
                            <img
                              src={img}
                              alt={`${ref.title} — vue ${j + 2}`}
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-[#003366] text-white relative overflow-hidden">
        <div className="absolute inset-0 diag-lines opacity-20 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <h2 className="font-display font-extrabold text-3xl md:text-5xl tracking-tight leading-[1]">
              Un projet en <span className="text-[#FF5A1F]">{service.title.toLowerCase()}</span> ?
            </h2>
            <p className="mt-5 text-slate-200/90 max-w-lg">
              Contactez-nous pour une visite technique gratuite et un devis
              sous 48 h.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-3">
            <a
              href="tel:0658116161"
              data-testid="service-cta-phone"
              className="group inline-flex items-center justify-between gap-4 bg-[#FF5A1F] hover:bg-[#E04812] text-white font-semibold px-6 py-4 transition-all hover:-translate-y-0.5"
            >
              <span className="inline-flex items-center gap-2">
                <Phone className="w-4 h-4" /> 06 58 11 61 61
              </span>
              <ArrowUpRight className="w-4 h-4" />
            </a>
            <a
              href="mailto:etancia.pro1@gmail.com"
              data-testid="service-cta-email"
              className="group inline-flex items-center justify-between gap-4 border-2 border-white/40 hover:border-white text-white font-semibold px-6 py-4 transition-all"
            >
              <span className="inline-flex items-center gap-2">
                <Mail className="w-4 h-4" /> Envoyer un mail
              </span>
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* OTHER SERVICES */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-block w-10 h-px bg-[#FF5A1F]" />
            <span className="label-kicker text-[#FF5A1F]">
              Nos autres expertises
            </span>
          </div>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight text-[#0F172A] leading-tight mb-10">
            Explorez nos autres prestations.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0 border-l border-t border-slate-200">
            {otherServices.map((s) => {
              const OtherIcon = s.icon;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`other-service-${s.slug}`}
                  className="group border-r border-b border-slate-200 p-6 lg:p-7 flex flex-col justify-between min-h-[220px] hover:bg-[#F2F4F7] transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="w-11 h-11 bg-[#003366] text-white flex items-center justify-center">
                      <OtherIcon className="w-5 h-5" />
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-slate-400 group-hover:text-[#FF5A1F] transition" />
                  </div>
                  <h3 className="mt-6 font-display font-bold text-base text-[#0F172A] leading-tight">
                    {s.title}
                  </h3>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
